import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import {
  THEME_PRESETS, DEFAULT_THEME_ID, THEME_LEVEL_MAP, migrateThemeId,
  BOX_COLORS_LIGHT, BOX_COLORS_PRO_DARK, BOX_COLORS_MINIMAL,
  BOX_COLORS_BW_DARK, BOX_COLORS_BW_LIGHT, BOX_COLORS_ADHD,
  BOX_COLORS_MMNTM_DARK, BOX_COLORS_MMNTM_LIGHT,
} from "./themePresets";
import { CRT_COLORS } from "./neonTheme";

const ThemeContext = createContext(null);
// Non-sensitive cosmetic preferences stored in localStorage (safe for XSS — no auth/PII data)
const STORAGE_KEY = "workstation-theme";
const OVERRIDES_KEY = "workstation-theme-overrides";
const CUSTOM_KEY = "workstation-custom-themes";
const COLOR_SCHEME_KEY = "workstation-color-scheme";
const AUTO_THEME_KEY = "workstation-auto-theme";

const VALID_SCHEMES = new Set(["dark", "light", "system"]);

function safeJSON(str, fallback) {
  try {
    const parsed = JSON.parse(str);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch { return fallback; }
}

function safeJSONObject(str, fallback) {
  try {
    const parsed = JSON.parse(str);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : fallback;
  } catch { return fallback; }
}

function sanitizeThemeId(raw) {
  if (typeof raw !== "string" || raw.length > 100) return null;
  return /^[a-zA-Z0-9_-]+$/.test(raw) ? raw : null;
}

function sanitizeScheme(raw) {
  return VALID_SCHEMES.has(raw) ? raw : "system";
}

function getSystemPreference() {
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
  }
  return "dark";
}

export function ThemeProvider({ children }) {
  // Color scheme: dark / light / system
  const [colorScheme, setColorSchemeRaw] = useState(
    () => sanitizeScheme(localStorage.getItem(COLOR_SCHEME_KEY))
  );
  const [systemPref, setSystemPref] = useState(getSystemPreference);
  const [autoTheme, setAutoThemeRaw] = useState(
    () => localStorage.getItem(AUTO_THEME_KEY) === "true"
  );

  // Listen for system preference changes
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const handler = (e) => setSystemPref(e.matches ? "light" : "dark");
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const effectiveScheme = colorScheme === "system" ? systemPref : colorScheme;

  // Auto-theme: switch theme when system preference changes
  useEffect(() => {
    if (!autoTheme) return;
    // After Feb 2026 consolidation: "Light" = minimalist-bw-dark, "Dark" = minimalist-bw
    const target = systemPref === "light" ? "minimalist-bw-dark" : "minimalist-bw";
    setActiveThemeId(target);
    setCustomOverrides(null);
  }, [autoTheme, systemPref]);

  const setAutoTheme = useCallback((enabled) => {
    setAutoThemeRaw(enabled);
    localStorage.setItem(AUTO_THEME_KEY, String(enabled));
    if (enabled) {
      setColorSchemeRaw("system");
      localStorage.setItem(COLOR_SCHEME_KEY, "system");
    }
  }, []);

  const setColorScheme = useCallback((scheme) => {
    const safe = sanitizeScheme(scheme);
    setColorSchemeRaw(safe);
    localStorage.setItem(COLOR_SCHEME_KEY, safe);
  }, []);

  const [activeThemeId, setActiveThemeId] = useState(() => {
    const stored = sanitizeThemeId(localStorage.getItem(STORAGE_KEY));
    if (!stored || stored === "retro-cyber") {
      return DEFAULT_THEME_ID;
    }
    // Apply Feb-2026 5-theme consolidation transparently for existing users
    return migrateThemeId(stored);
  });

  const [customOverrides, setCustomOverrides] = useState(
    () => safeJSONObject(localStorage.getItem(OVERRIDES_KEY), null)
  );
  const [customThemes, setCustomThemes] = useState(
    () => safeJSON(localStorage.getItem(CUSTOM_KEY), [])
  );

  const allThemes = useMemo(() => [...THEME_PRESETS, ...customThemes], [customThemes]);

  // MMNTM is always dark — no auto-switching based on system preference
  // The brand identity demands a dark command center aesthetic

  const baseTheme = useMemo(
    () => allThemes.find((t) => t.id === activeThemeId) || THEME_PRESETS[0],
    [allThemes, activeThemeId]
  );

  const tokens = useMemo(() => {
    const base = customOverrides ? { ...baseTheme.tokens, ...customOverrides } : baseTheme.tokens;
    // Professional / MMNTM enforcement: no glow, no scanlines
    if (baseTheme.styleType === "professional") {
      return {
        ...base,
        glowIntensity: base.glowIntensity || 0,
        scanlines: false,
        success: base.success || "#22C55E",
        danger: base.danger || "#DC2626",
        warning: base.warning || "#F59E0B",
      };
    }
    if (baseTheme.styleType === "adhd") {
      return { ...base, glowIntensity: 0, scanlines: false, fontScale: base.fontScale || 1.1 };
    }
    if (baseTheme.styleType === "minimal") {
      return { ...base, glowIntensity: 0, scanlines: false };
    }
    return base;
  }, [baseTheme, customOverrides]);

  const boxColors = useMemo(() => {
    const st = baseTheme.styleType || "professional";
    const isDark = tokens.isDark;
    const id = baseTheme.id;
    // MMNTM Core themes get their own box colors
    if (id === "mmntm-dark" || id === "mmntm-light-true" || id === "midnight-purple" || id === "deep-navy" || id === "forest") {
      return isDark ? BOX_COLORS_MMNTM_DARK : BOX_COLORS_MMNTM_LIGHT;
    }
    if (id === "mmntm-light") return BOX_COLORS_MMNTM_LIGHT;
    if (st === "adhd") return BOX_COLORS_ADHD;
    if (st === "minimal") {
      if (id?.startsWith("minimalist-bw")) return isDark ? BOX_COLORS_BW_DARK : BOX_COLORS_BW_LIGHT;
      return isDark ? BOX_COLORS_MINIMAL : BOX_COLORS_LIGHT;
    }
    if (st === "professional") return isDark ? BOX_COLORS_PRO_DARK : BOX_COLORS_LIGHT;
    if (!isDark) return BOX_COLORS_LIGHT;
    return CRT_COLORS;
  }, [tokens.isDark, baseTheme.styleType, baseTheme.id]);

  // Apply CSS variables to :root
  useEffect(() => {
    const r = document.documentElement.style;
    r.setProperty("--tm-bg-primary", tokens.bgPrimary);
    r.setProperty("--tm-bg-secondary", tokens.bgSecondary);
    r.setProperty("--tm-bg-tertiary", tokens.bgTertiary);
    r.setProperty("--tm-bg-card", tokens.bgCard);
    r.setProperty("--tm-text-primary", tokens.textPrimary);
    r.setProperty("--tm-text-secondary", tokens.textSecondary);
    r.setProperty("--tm-text-muted", tokens.textMuted);
    r.setProperty("--tm-accent", tokens.accent);
    r.setProperty("--tm-accent-2", tokens.accent2);
    r.setProperty("--tm-accent-3", tokens.accent3);
    r.setProperty("--tm-accent-4", tokens.accent4);
    r.setProperty("--tm-success", tokens.success);
    r.setProperty("--tm-danger", tokens.danger);
    r.setProperty("--tm-warning", tokens.warning);
    r.setProperty("--tm-border-color", tokens.borderColor);
    r.setProperty("--tm-border-radius", tokens.borderRadius + "px");
    r.setProperty("--tm-border-width", tokens.borderWidth + "px");
    r.setProperty("--tm-font-heading", tokens.fontHeading);
    r.setProperty("--tm-font-body", tokens.fontBody);
    r.setProperty("--tm-glow-intensity", String(tokens.glowIntensity));
    r.setProperty("--tm-scanline-opacity", tokens.scanlines ? "1" : "0");
    r.setProperty("--tm-header-bg", tokens.headerBg);
    r.setProperty("--tm-header-border", tokens.headerBorder);
    const shadows = {
      flat: "none",
      bordered: "none",
      elevated: tokens.isDark
        ? "0 4px 24px rgba(0,0,0,0.4)"
        : "0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)",
      glass: "0 4px 30px rgba(0,0,0,0.3)",
    };
    r.setProperty("--tm-card-shadow", shadows[tokens.cardStyle] || "none");
    r.setProperty("--tm-card-backdrop", tokens.cardStyle === "glass" ? "blur(12px)" : "none");
    r.setProperty("--tm-card-bg-opacity", tokens.cardStyle === "glass" ? "0.7" : "1");
  }, [tokens]);

  // Persist
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, activeThemeId);
    if (customOverrides) {
      localStorage.setItem(OVERRIDES_KEY, JSON.stringify(customOverrides));
    } else {
      localStorage.removeItem(OVERRIDES_KEY);
    }
  }, [activeThemeId, customOverrides]);

  useEffect(() => {
    localStorage.setItem(CUSTOM_KEY, JSON.stringify(customThemes));
  }, [customThemes]);

  const setTheme = useCallback((id) => {
    setActiveThemeId(migrateThemeId(id));
    setCustomOverrides(null);
  }, []);

  const updateTokens = useCallback((updates) => {
    setCustomOverrides((prev) => ({ ...prev, ...updates }));
  }, []);

  const saveCustomTheme = useCallback(
    (name) => {
      const newTheme = {
        id: `custom-${Date.now()}`,
        name,
        category: "custom",
        premium: false,
        description: "Custom theme",
        preview: { bg: tokens.bgPrimary, accent: tokens.accent, accent2: tokens.accent2 },
        tokens: { ...tokens },
      };
      setCustomThemes((prev) => [...prev, newTheme]);
      setActiveThemeId(newTheme.id);
      setCustomOverrides(null);
      return newTheme;
    },
    [tokens]
  );

  const duplicateTheme = useCallback(
    (themeId) => {
      const source = allThemes.find((t) => t.id === themeId);
      if (!source) return;
      const dup = {
        ...source,
        id: `custom-${Date.now()}`,
        name: `${source.name} (Copy)`,
        category: "custom",
        premium: false,
        tokens: { ...source.tokens },
      };
      setCustomThemes((prev) => [...prev, dup]);
      setActiveThemeId(dup.id);
      setCustomOverrides(null);
    },
    [allThemes]
  );

  const deleteCustomTheme = useCallback(
    (id) => {
      setCustomThemes((prev) => prev.filter((t) => t.id !== id));
      if (activeThemeId === id) {
        setActiveThemeId(DEFAULT_THEME_ID);
        setCustomOverrides(null);
      }
    },
    [activeThemeId]
  );

  const resetToDefault = useCallback(() => {
    // Light/Dark map to the consolidated minimalist B&W presets (Feb 2026)
    setActiveThemeId(effectiveScheme === "light" ? "minimalist-bw-dark" : "minimalist-bw");
    setCustomOverrides(null);
  }, [effectiveScheme]);

  const resetOverrides = useCallback(() => {
    setCustomOverrides(null);
  }, []);

  // Toggle dark/light
  const toggleColorScheme = useCallback(() => {
    const next = effectiveScheme === "dark" ? "light" : "dark";
    setColorScheme(next);
  }, [effectiveScheme, setColorScheme]);

  // --- Split value into focused sub-memos (≤5 deps each) ---
  const themeData = useMemo(() => ({
    tokens, boxColors, styleType: baseTheme.styleType || "professional",
    activeThemeId, activeTheme: baseTheme,
  }), [tokens, boxColors, baseTheme, activeThemeId]);

  const themeCollections = useMemo(() => ({
    allThemes, customOverrides, hasOverrides: !!customOverrides,
  }), [allThemes, customOverrides]);

  const schemeState = useMemo(() => ({
    colorScheme, effectiveScheme, autoTheme,
  }), [colorScheme, effectiveScheme, autoTheme]);

  const schemeActions = useMemo(() => ({
    setAutoTheme, setColorScheme, toggleColorScheme,
  }), [setAutoTheme, setColorScheme, toggleColorScheme]);

  const themeWrite = useMemo(() => ({
    setTheme, updateTokens, saveCustomTheme,
  }), [setTheme, updateTokens, saveCustomTheme]);

  const themeManage = useMemo(() => ({
    duplicateTheme, deleteCustomTheme, resetToDefault, resetOverrides,
  }), [duplicateTheme, deleteCustomTheme, resetToDefault, resetOverrides]);

  const themeState = useMemo(
    () => ({ ...themeData, ...themeCollections, ...schemeState }),
    [themeData, themeCollections, schemeState]
  );

  const themeOps = useMemo(
    () => ({ ...schemeActions, ...themeWrite, ...themeManage }),
    [schemeActions, themeWrite, themeManage]
  );

  const value = useMemo(
    () => ({ ...themeState, ...themeOps }),
    [themeState, themeOps]
  );

  return (
    <ThemeContext.Provider value={value}>
      <div style={{ zoom: tokens.fontScale || 1, "--tm-zoom": tokens.fontScale || 1 }}>{children}</div>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
